<template lang="pug">
.collection-halo-icon(:style="{ width: `${size}px`, height: `${size}px` }")
  svg.halo-ring(
    :width="size"
    :height="size"
    :viewBox="`0 0 ${size} ${size}`"
  )
    circle(
      :cx="center"
      :cy="center"
      :r="radius"
      fill="none"
      stroke="#f0f0f0"
      :stroke-width="strokeWidth"
    )
    circle(
      v-if="showProgress && progress > 0"
      :cx="center"
      :cy="center"
      :r="radius"
      fill="none"
      :stroke="ringColor"
      :stroke-width="strokeWidth"
      :stroke-dasharray="circumference"
      :stroke-dashoffset="dashOffset"
      stroke-linecap="round"
    )
  .halo-center(
    :class="{ 'is-complete': isComplete }"
  )
    img(
      v-if="imageSrc && !imageFailed"
      :src="imageSrc"
      :alt="title"
      @error="onImageError"
    )
    span.halo-initials(v-else) {{ initials }}
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';

const ACCENT = '#0d9488';

export default defineComponent({
  name: 'CollectionHaloIcon',
  props: {
    coverImageUrl: {
      type: String,
      default: '',
    },
    ownerAvatar: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    collectedCount: {
      type: Number,
      required: true,
    },
    totalStickers: {
      type: Number,
      required: true,
    },
    isCompleted: {
      type: Boolean,
      default: false,
    },
    showProgress: {
      type: Boolean,
      default: true,
    },
    size: {
      type: Number,
      default: 76,
    },
  },
  setup(props) {
    const imageFailed = ref(false);
    const strokeWidth = 4;
    const radius = 33;

    const center = computed(() => props.size / 2);
    const circumference = computed(() => 2 * Math.PI * radius);

    const progress = computed(() => {
      if (props.totalStickers <= 0) return 0;
      return Math.min(1, props.collectedCount / props.totalStickers);
    });

    const isComplete = computed(() => props.isCompleted || progress.value >= 1);

    const ringColor = computed(() => (isComplete.value ? ACCENT : ACCENT));

    const dashOffset = computed(
      () => circumference.value * (1 - progress.value),
    );

    const imageSrc = computed(
      () => props.coverImageUrl || props.ownerAvatar || '',
    );

    const initials = computed(() => {
      const text = props.title?.trim() || '?';
      return text.charAt(0).toUpperCase();
    });

    function onImageError() {
      imageFailed.value = true;
    }

    return {
      strokeWidth,
      radius,
      center,
      circumference,
      progress,
      isComplete,
      ringColor,
      dashOffset,
      imageSrc,
      imageFailed,
      initials,
      onImageError,
    };
  },
});
</script>

<style lang="scss" scoped>
.collection-halo-icon {
  position: relative;
  flex-shrink: 0;
}

.halo-ring {
  position: absolute;
  inset: 0;
  transform: rotate(-90deg);
}

.halo-center {
  position: absolute;
  inset: 8px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(13, 148, 136, 0.07);
  display: flex;
  align-items: center;
  justify-content: center;

  &.is-complete {
    background: #e6f9f9;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.halo-initials {
  font-size: 1.25rem;
  font-weight: 800;
  color: #1a2744;
}
</style>
